import React, { useMemo } from "react";

export type OrbState =
  | "idle"
  | "wake"
  | "thinking"
  | "speaking"
  | "acting"
  | "blocked"
  | "confirming"
  | "error";

interface JarvisOrbProps {
  state: OrbState;
  size?: number;
  assistantName?: string;
}

interface StateProfile {
  label: string;
  glow: string;
  glowSoft: string;
  ringSpinSec: number;
  innerRingSpinSec: number;
  particlesSpinSec: number;
  pulseSec: number;
  floatSec: number;
  hueRotate: number;
  brightness: number;
}

// Farben aus dem Asset-Pack (gold/amber) plus State-Tönungen via filter hueRotate.
const PROFILES: Record<OrbState, StateProfile> = {
  idle: {
    label: "bereit",
    glow: "rgba(255,178,28,0.55)",
    glowSoft: "rgba(255,122,0,0.30)",
    ringSpinSec: 14,
    innerRingSpinSec: 9,
    particlesSpinSec: 22,
    pulseSec: 3.2,
    floatSec: 5.2,
    hueRotate: 0,
    brightness: 0.95,
  },
  wake: {
    label: "hört zu",
    glow: "rgba(106,214,255,0.85)",
    glowSoft: "rgba(56,160,255,0.45)",
    ringSpinSec: 5,
    innerRingSpinSec: 3,
    particlesSpinSec: 9,
    pulseSec: 1.6,
    floatSec: 3.2,
    hueRotate: 180,
    brightness: 1.15,
  },
  thinking: {
    label: "denkt",
    glow: "rgba(255,200,40,1)",
    glowSoft: "rgba(255,130,0,0.6)",
    ringSpinSec: 4,
    innerRingSpinSec: 2.5,
    particlesSpinSec: 6,
    pulseSec: 1.8,
    floatSec: 4.0,
    hueRotate: 10,
    brightness: 1.2,
  },
  speaking: {
    label: "spricht",
    glow: "rgba(64,255,180,0.95)",
    glowSoft: "rgba(20,200,140,0.55)",
    ringSpinSec: 6,
    innerRingSpinSec: 3.5,
    particlesSpinSec: 11,
    pulseSec: 0.95,
    floatSec: 3.4,
    hueRotate: 90,
    brightness: 1.15,
  },
  acting: {
    label: "arbeitet",
    glow: "rgba(255,140,30,1)",
    glowSoft: "rgba(255,80,0,0.65)",
    ringSpinSec: 2.5,
    innerRingSpinSec: 1.6,
    particlesSpinSec: 4,
    pulseSec: 1.2,
    floatSec: 3.0,
    hueRotate: -10,
    brightness: 1.3,
  },
  blocked: {
    label: "wartet",
    glow: "rgba(255,166,79,0.9)",
    glowSoft: "rgba(255,110,40,0.45)",
    ringSpinSec: 10,
    innerRingSpinSec: 6,
    particlesSpinSec: 14,
    pulseSec: 1.8,
    floatSec: 3.6,
    hueRotate: -4,
    brightness: 1.05,
  },
  confirming: {
    label: "wartet auf Go",
    glow: "rgba(120,255,140,0.85)",
    glowSoft: "rgba(60,200,100,0.45)",
    ringSpinSec: 8,
    innerRingSpinSec: 5,
    particlesSpinSec: 13,
    pulseSec: 1.4,
    floatSec: 4.6,
    hueRotate: 110,
    brightness: 1.05,
  },
  error: {
    label: "Fehler",
    glow: "rgba(255,89,89,0.9)",
    glowSoft: "rgba(220,30,30,0.55)",
    ringSpinSec: 16,
    innerRingSpinSec: 10,
    particlesSpinSec: 24,
    pulseSec: 0.6,
    floatSec: 3.8,
    hueRotate: -120,
    brightness: 1.05,
  },
};

export function JarvisOrb({ state, size = 320, assistantName = "SIMO" }: JarvisOrbProps): JSX.Element {
  const p = PROFILES[state];

  const style = useMemo(
    () =>
      ({
        "--orb-size": `${size}px`,
        "--orb-glow": p.glow,
        "--orb-glow-soft": p.glowSoft,
        "--orb-ring-spin": `${p.ringSpinSec}s`,
        "--orb-ring-spin-inner": `${p.innerRingSpinSec}s`,
        "--orb-particles-spin": `${p.particlesSpinSec}s`,
        "--orb-pulse": `${p.pulseSec}s`,
        "--orb-float": `${p.floatSec}s`,
        "--orb-hue": `${p.hueRotate}deg`,
        "--orb-brightness": `${p.brightness}`,
      }) as React.CSSProperties,
    [p, size],
  );

  return (
    <div className="jarvis-orb-stage" style={style}>
      {/* Outer halo */}
      <div className="jarvis-orb-halo" />

      {/* Particles layer (Orbit-Bewegung) */}
      <img
        className="jarvis-orb-layer jarvis-orb-particles"
        src="/assets/jarvis/layer_particles_1024.png"
        alt=""
        draggable={false}
      />

      {/* Outer ring (counter-spin) */}
      <img
        className="jarvis-orb-layer jarvis-orb-ring-outer"
        src="/assets/jarvis/layer_rings_1024.png"
        alt=""
        draggable={false}
      />

      {/* Core (state-gefärbt, pulsiert) */}
      <img
        className="jarvis-orb-layer jarvis-orb-core"
        src="/assets/jarvis/it-simo-jarvis-orb_1024_transparent.png"
        alt={`${assistantName} ${state}`}
        draggable={false}
      />

      {/* Flare overlay */}
      <img
        className="jarvis-orb-layer jarvis-orb-flare"
        src="/assets/jarvis/layer_flare_1024.png"
        alt=""
        draggable={false}
      />

      <div className="jarvis-orb-label">{p.label}</div>
    </div>
  );
}
